import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const IntegrationModeContext = createContext(null);

// Integration modes: 'assist' | 'fhir' | 'enable'
const DEFAULTS = {
  mode: 'assist',
  ehrName: null,
  firstRun: {},       // { lumina: true, echo: true, revq: true }
  milestones: {},     // { firstNoteSigned: false, firstAICall: false, firstDenialPrevented: false }
  nudgeDismissed: {}, // { fhir_schedule: false, fhir_claims: false }
  lensUploadCount: 0,
  weeklySummaryDismissed: null, // ISO date string of last dismissed Monday
};

const STORAGE_KEY = 'ariaone-integration-mode';

export const IntegrationModeProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return { ...DEFAULTS, ...saved };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const setMode = useCallback((mode, ehrName) => {
    setState(prev => ({ ...prev, mode, ehrName: ehrName || prev.ehrName }));
  }, []);

  const isFirstRun = useCallback((app) => {
    return state.firstRun[app] !== false;
  }, [state.firstRun]);

  const dismissFirstRun = useCallback((app) => {
    setState(prev => ({ ...prev, firstRun: { ...prev.firstRun, [app]: false } }));
  }, []);

  const completeMilestone = useCallback((key) => {
    setState(prev => ({ ...prev, milestones: { ...prev.milestones, [key]: true } }));
  }, []);

  const hasMilestone = useCallback((key) => {
    return !!state.milestones[key];
  }, [state.milestones]);

  const dismissNudge = useCallback((key) => {
    setState(prev => ({ ...prev, nudgeDismissed: { ...prev.nudgeDismissed, [key]: true } }));
  }, []);

  const isNudgeDismissed = useCallback((key) => {
    return !!state.nudgeDismissed[key];
  }, [state.nudgeDismissed]);

  const incrementLensUpload = useCallback(() => {
    setState(prev => ({ ...prev, lensUploadCount: prev.lensUploadCount + 1 }));
  }, []);

  const dismissWeeklySummary = useCallback(() => {
    const today = new Date();
    setState(prev => ({ ...prev, weeklySummaryDismissed: today.toISOString().split('T')[0] }));
  }, []);

  const shouldShowWeeklySummary = useCallback(() => {
    const today = new Date();
    if (today.getDay() !== 1) return false; // Only on Mondays
    const dismissed = state.weeklySummaryDismissed;
    if (!dismissed) return true;
    return dismissed !== today.toISOString().split('T')[0];
  }, [state.weeklySummaryDismissed]);

  const value = {
    mode: state.mode,
    ehrName: state.ehrName,
    lensUploadCount: state.lensUploadCount,
    setMode,
    isFirstRun,
    dismissFirstRun,
    completeMilestone,
    hasMilestone,
    dismissNudge,
    isNudgeDismissed,
    incrementLensUpload,
    dismissWeeklySummary,
    shouldShowWeeklySummary,
    isAssist: state.mode === 'assist',
    isFHIR: state.mode === 'fhir',
    isEnable: state.mode === 'enable',
  };

  return (
    <IntegrationModeContext.Provider value={value}>
      {children}
    </IntegrationModeContext.Provider>
  );
};

export const useIntegrationMode = () => {
  const ctx = useContext(IntegrationModeContext);
  if (!ctx) throw new Error('useIntegrationMode must be used within IntegrationModeProvider');
  return ctx;
};
